import { useCurrentId } from "hooks/useCurrentId.ts"
import { ConfigurationCustomize } from "pages/ConfigurationPage/ConfigurationCustomize"
import { ConfigurationFeatures } from "pages/ConfigurationPage/ConfigurationFeatures"
import { ConfigurationSdkToken } from "pages/ConfigurationPage/ConfigurationSdkToken"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { typedEnv } from "../../config/typedEnv"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  Link,
  LinkButton,
  Tab,
  TabGroup,
  TabPanel,
  TabPanels,
  Tabs,
} from "@strapi/design-system"
import { ArrowLeft, Check, Eye } from "@strapi/icons"
import { useMerchantQuery, useSaveMerchantMutation } from "hooks/query/useMerchantQuery.ts"
import PageLoader from "components/PageLoader"
import { useBrandsQuery } from "hooks/query/useBrandsQuery.ts"

const tabsTemplate = (t: (key: string) => string) => [
  {
    title: t("configurationPage.features"),
    subTitle: t("configurationPage.features"),
    key: "features",
    Component: ConfigurationFeatures,
  },
  {
    title: t("configurationPage.sdkToken"),
    subTitle: t("configurationPage.sdkToken"),
    key: "sdkToken",
    Component: ConfigurationSdkToken,
  },
  {
    title: t("configurationPage.customize"),
    subTitle: t("configurationPage.customize"),
    key: "customize",
    Component: ConfigurationCustomize,
  },
]
export const ConfigurationPage = () => {
  const { t } = useTranslation()
  const [currentTab, setCurrentTab] = useState(0)
  const [data, setData] = useState(null)
  const [validData, setValidData] = useState<Record<string, boolean>>({})
  const { isLoading: brandsAreLoading } = useBrandsQuery()
  const merchantId = useCurrentId()

  const { data: merchant, isLoading: merchantLoading } = useMerchantQuery(merchantId)

  const { mutate: saveMerchant, isPending: saveLoading } = useSaveMerchantMutation(merchantId)

  useEffect(() => {
    if (merchant) {
      const { config, sdkToken } = JSON.parse(merchant.token)
      setData({
        config: JSON.parse(config),
        sdkToken,
      })

      // Initialize validData state based on available tabs
      setValidData(tabsTemplate(t).reduce((acc, { key }) => ({ ...acc, [key]: true }), {}))
    }
  }, [merchant, t])

  const valid = useMemo(() => Object.values(validData).every((value) => value), [validData])

  const onSave = useCallback(() => {
    saveMerchant({
      ...merchant,
      token: JSON.stringify({
        config: JSON.stringify(data.config),
        sdkToken: data.sdkToken,
      }),
    })
  }, [data, merchant, saveMerchant])

  return (
    <Box>
      <BaseHeaderLayout
        title={`${t("configurationPage.merchantConfiguration")} "${merchant?.name || ""}"`}
        subtitle={tabsTemplate(t)[currentTab].subTitle}
        as="h1"
        navigationAction={
          <Link startIcon={<ArrowLeft />} to={`/merchants`}>
            {t("configurationPage.backToMerchants")}
          </Link>
        }
        primaryAction={
          <Flex gap={2}>
            <LinkButton
              startIcon={<Eye />}
              variant="tertiary"
              href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?merchantId=${merchantId}`}
            >
              {t("previewTintVto")}
            </LinkButton>
            <Button startIcon={<Check />} loading={saveLoading} onClick={onSave} disabled={!valid}>
              {t("configurationPage.save")}
            </Button>
          </Flex>
        }
      />
      <ContentLayout>
        {merchantLoading || brandsAreLoading || !data ? (
          <PageLoader />
        ) : (
          <TabGroup
            label={t("configurationPage.configurationTabs")}
            id="tabs"
            onTabChange={(selected) => setCurrentTab(selected)}
            variant="simple"
            initialSelectedTabIndex={currentTab}
          >
            <Tabs>
              {tabsTemplate(t).map(({ title, key }) => (
                <Tab hasError={!validData[key]} key={key}>
                  {title}
                </Tab>
              ))}
            </Tabs>
            <TabPanels>
              {tabsTemplate(t).map(({ Component, key }) => (
                <Box paddingTop={4} key={key}>
                  <TabPanel color="neutral800" padding={4} background="neutral0">
                    <Component
                      data={data}
                      onChange={setData}
                      onValid={(valid) => setValidData((prev) => ({ ...prev, [key]: valid }))}
                    />
                  </TabPanel>
                </Box>
              ))}
            </TabPanels>
          </TabGroup>
        )}
      </ContentLayout>
    </Box>
  )
}
